/**
 * AgentEditDialog
 * Edits the small set of fields that don't belong inline on the Overview
 * tab: name, description, agent type, active, template. The system prompt
 * and LLM/embedding model are edited inline on the Overview tab itself.
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { AgentDetail as AgentDetailType, AgentType } from '../../../types';

const AGENT_TYPE_OPTIONS: Array<{ value: AgentType; label: string }> = [
  { value: 'ASSISTANT', label: 'Assistant' },
  { value: 'AI_SYSTEM_BUILDER', label: 'AI System Builder' },
  { value: 'MAIN_ASSISTANT', label: 'Main Assistant' },
  { value: 'KNOWLEDGE_GRAPH', label: 'Knowledge Graph' },
  { value: 'BOT_BUILDER', label: 'Bot Builder' },
  { value: 'MODEL_LAB_BUILDER', label: 'Model Lab Builder' },
];

export interface AgentEditValues {
  /** Injected from the current agent so the serializer stays valid. */
  model_id: number;
  system_prompt: string;
  // User-editable fields:
  name: string;
  description: string;
  agent_type: AgentType;
  is_active: boolean;
  is_default: boolean;
  is_template: boolean;
}

interface AgentEditDialogProps {
  open: boolean;
  agent: AgentDetailType;
  isStaff: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: AgentEditValues) => void;
}

export const AgentEditDialog: React.FC<AgentEditDialogProps> = ({
  open,
  agent,
  isStaff,
  saving,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description || '');
  const [agentType, setAgentType] = useState<AgentEditValues['agent_type']>(
    agent.agent_type || 'ASSISTANT'
  );
  const [isActive, setIsActive] = useState(agent.is_active);
  const [isDefault, setIsDefault] = useState(Boolean(agent.is_default));
  const [isTemplate, setIsTemplate] = useState(agent.is_template || false);

  useEffect(() => {
    if (!open) return;
    setName(agent.name);
    setDescription(agent.description || '');
    setAgentType(agent.agent_type || 'ASSISTANT');
    setIsActive(agent.is_active);
    setIsDefault(Boolean(agent.is_default));
    setIsTemplate(agent.is_template || false);
  }, [open, agent]);

  const canSave = !!name.trim();

  const handleSubmit = () => {
    onSubmit({
      model_id: agent.model?.id,
      system_prompt: agent.system_prompt || '',
      name: name.trim(),
      description,
      agent_type: agentType,
      is_active: isActive,
      is_default: isDefault && isActive,
      is_template: isTemplate,
    } as AgentEditValues);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit agent details</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The system prompt and models are edited inline on the Overview tab.
        </Typography>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Agent type</InputLabel>
            <Select
              value={agentType}
              label="Agent type"
              onChange={(e) =>
                setAgentType(e.target.value as AgentEditValues['agent_type'])
              }
            >
              {AGENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isDefault && isActive}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={!isActive}
              />
            }
            label={
              isTemplate
                ? 'Default template for this agent type'
                : 'Default for this agent type'
            }
          />
          {isStaff ? (
            <FormControlLabel
              control={
                <Switch
                  checked={isTemplate}
                  onChange={(e) => setIsTemplate(e.target.checked)}
                  color="warning"
                />
              }
              label="Template (admin only)"
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSave || saving}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentEditDialog;
